import { ProgramConfig, Timezone, validTimezones } from '../survey';

/**
 * Monday-first because that's how a schedule reads to a person. Anything indexing by
 * `Date.getUTCDay()` needs the Sunday-first order instead — see `dayNames` in aidkit's cron.
 */
export const daysOfWeek = [
  'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday',
] as const;

export type DayOfWeek = typeof daysOfWeek[number];

/**
 * Whether a value is an hour on a 24-hour clock. Callers hold hours as plain numbers, so this is
 * what stands between a hand-edited survey (or a bad request) and a schedule that never fires.
 *
 * Note that `0` is a valid hour, which rules out the usual truthiness check.
 */
export function isHourOfDay(hour: unknown): hour is number {
  return typeof hour === 'number' && Number.isInteger(hour) && hour >= 0 && hour <= 23;
}

/** When a conversation review summary goes out, absent an hour on the config itself. */
export const DEFAULT_SUMMARY_SEND_HOUR = 9;

/** Which day a weekly conversation review summary goes out, absent a day on the config itself. */
export const DEFAULT_SUMMARY_DAY_OF_WEEK: DayOfWeek = 'Monday';

/**
 * Last resort when nothing says what timezone the program runs on, matching the
 * `timezone || 'America/New_York'` convention in program/notification.ts.
 */
export const DEFAULT_SUMMARY_TIMEZONE: Timezone = 'America/New_York';

/**
 * Whether a value is one of the zones the survey schema allows. Survey JSON isn't validated at
 * runtime, so this is what stands between a hand-edited zone and the `RangeError` `Intl` throws on
 * one it doesn't recognize.
 */
export function isTimezone(timezone: unknown): timezone is Timezone {
  return validTimezones.includes(timezone as Timezone);
}

export function getDefaultTimezone(
  programConfig?: Pick<Partial<ProgramConfig>, 'comms'>
): Timezone {
  const inherited = programConfig?.comms?.replyScheduling?.timezone;
  return isTimezone(inherited) ? inherited : DEFAULT_SUMMARY_TIMEZONE;
}
